/**
 * #5345 Story 6.1 — THE TARGET CONTRACT THE MODULE RUNS ON.
 *
 * One number in a request ("15%") can mean four different governed things. The
 * defect was not a missing regex: it was that the manager minted a target
 * reference from the reader's words and the finance module then re-read the same
 * words with four patterns of its own, so one fact had two authorities and the
 * weaker one won.
 *
 * The correction is a reference, not a richer object on the wire. The plan names
 * `targetRef`. The SERVER resolves that reference out of the turn's trusted set
 * and materializes the immutable value below. The module consumes it and reads
 * no sentence. A model may select an id; it may never author, copy or alter any
 * field here.
 */
import type { FinanceEbitdaTargetType } from './approved-formulas';
import { FINANCE_EBITDA_TARGET_TYPES } from './approved-formulas';
import type { CapabilityRequirementUnit } from './ask-helix/contracts/governed-capability-manifest';

/**
 * The asker-stated OBJECTIVE re-parameterization, kept separate from the EBITDA
 * margin family on purpose (ChatGPT AMEND v3 item 3): these two derive the target
 * from the approved OBJECTIVE baseline, not from the EBITDA margin, and collapsing
 * them into one enum is exactly the drift that contract correction forbids.
 *
 * #5683 HOLD-3 — `objective_absolute_usd` and `objective_improvement_usd` are
 * two DIFFERENT governed meanings a USD citation can carry against the same
 * baseline, never aliases of one another:
 *
 *   `objective_absolute_usd`     the target IS this value       (a LEVEL)
 *   `objective_improvement_usd`  the target is baseline + this  (a DELTA)
 *
 * A stated "$5M" can lawfully mean either. Collapsing them loses the
 * distinction structurally, so both are minted as their own typed id and the
 * model SELECTS which one the request meant — it is never inferred from
 * wording or from which one happens to be minted first.
 */
export const FINANCE_OBJECTIVE_REPARAMETERIZATION_TARGET_TYPES = [
  'objective_growth_pct',
  'objective_absolute_usd',
  'objective_improvement_usd',
] as const;

export type FinanceObjectiveReparameterizationTargetType =
  (typeof FINANCE_OBJECTIVE_REPARAMETERIZATION_TARGET_TYPES)[number];

/** Every governed target meaning a finance operation can execute on. */
export type FinanceObjectiveTargetType =
  | FinanceEbitdaTargetType
  | FinanceObjectiveReparameterizationTargetType;

export const FINANCE_OBJECTIVE_TARGET_TYPES: readonly FinanceObjectiveTargetType[] =
  Object.freeze([
    ...FINANCE_EBITDA_TARGET_TYPES,
    ...FINANCE_OBJECTIVE_REPARAMETERIZATION_TARGET_TYPES,
  ]);

export function isFinanceObjectiveTargetType(
  value: unknown
): value is FinanceObjectiveTargetType {
  return (FINANCE_OBJECTIVE_TARGET_TYPES as readonly unknown[]).includes(value);
}

/**
 * The interpretation policy this build mints and computes under. It travels on
 * every minted reference and on the materialized input, so a stored answer can
 * always say which policy produced its number.
 */
export const FINANCE_TARGET_INTERPRETATION_POLICY_VERSION =
  'finance_target_interpretation_policy_v1';

/**
 * The typed assumptions the DEFAULT policy may apply, as a closed set. An
 * assumption id is minted only where the policy actually chose for the reader;
 * a reading the reader stated unambiguously carries none.
 */
export const FINANCE_TARGET_INTERPRETATION_ASSUMPTION_IDS = [
  'percent_interpreted_as_relative_margin_change',
] as const;

export type FinanceTargetInterpretationAssumptionId =
  (typeof FINANCE_TARGET_INTERPRETATION_ASSUMPTION_IDS)[number];

export function isFinanceTargetInterpretationAssumptionId(
  value: unknown
): value is FinanceTargetInterpretationAssumptionId {
  return (
    FINANCE_TARGET_INTERPRETATION_ASSUMPTION_IDS as readonly unknown[]
  ).includes(value);
}

/** Where the target's authority comes from. Evaluated from the reference. */
export const FINANCE_TARGET_PROVENANCES = ['cited', 'approved'] as const;

export type FinanceTargetProvenance =
  (typeof FINANCE_TARGET_PROVENANCES)[number];

/**
 * The horizon the target is read against when the turn declares none. Named and
 * typed so an absent horizon is a DISCLOSED modeled default, never silence.
 */
export const FINANCE_TARGET_DEFAULT_HORIZON_ID =
  'finance_current_in_year_target_frame';

export const FINANCE_TARGET_HORIZON_BASES = [
  'declared',
  'manifest_default',
] as const;

export type FinanceTargetHorizonBasis =
  (typeof FINANCE_TARGET_HORIZON_BASES)[number];

/**
 * The reporting cut an approved target was admitted against, plus the approval
 * instant of the binding itself (#7554 P1): evidence for an approved target
 * carries the binding's OWN provenance, never the P&L cut's or the request's.
 */
export type FinanceApprovedTargetReportingIdentity = Readonly<{
  objectiveBindingId: string;
  sourceArtifactId: string;
  scopeKind: string;
  scopeId: string;
  periodType: 'annual';
  fiscalYear: number;
  accountingBasis: string;
  /** ISO instant the objective binding was approved. */
  approvedAt?: string;
}>;

/**
 * The server-materialized target a finance operation executes on. Every field is
 * server-derived from one trusted reference plus the governed objective; the
 * plan carries none of them.
 */
export type FinanceObjectiveTargetExecutionInput = Readonly<{
  targetRef: string;
  metricRef: string;
  targetType: FinanceObjectiveTargetType;
  targetValue: number;
  unit: CapabilityRequirementUnit;
  direction: 'increase' | 'decrease' | 'maintain';
  comparator: 'at_least' | 'at_most' | 'exactly';
  provenance: FinanceTargetProvenance;
  interpretationPolicyVersion: string;
  interpretationAssumptionId?: FinanceTargetInterpretationAssumptionId;
  horizonId: string;
  horizonBasis: FinanceTargetHorizonBasis;
  reportingIdentity?: FinanceApprovedTargetReportingIdentity;
}>;

/**
 * The ONE key a server-materialized target arrives on. It is not an argument:
 * the capability validator's approved argument set never contains it, and the
 * executor writes it after validation.
 */
export const FINANCE_OBJECTIVE_TARGET_EXECUTION_INPUT_KEY =
  'objectiveTargetExecutionInput';

/**
 * Kill pins (#5345 v4 item 6). Any of these on a planned operation's arguments
 * is a second target authority trying to exist beside `targetRef`, and the plan
 * is rejected rather than merged. `question`, `legacyTarget` and `legacyQuestion`
 * are here because a re-opened sentence path is exactly the regression this
 * story deletes.
 */
export const FINANCE_TARGET_SHAPED_ARGUMENT_KEYS: readonly string[] =
  Object.freeze([
    'target',
    'targetType',
    'targetValue',
    'value',
    'unit',
    'comparator',
    'direction',
    'provenance',
    'interpretationPolicyVersion',
    'interpretationAssumptionId',
    'horizonId',
    'horizonBasis',
    'question',
    'userIntent',
    'legacyTarget',
    'legacyQuestion',
    FINANCE_OBJECTIVE_TARGET_EXECUTION_INPUT_KEY,
  ] as const);

/**
 * One lawful reading of a stated target: which governed meaning it is, and the
 * typed assumption the default policy applied to choose it.
 */
export type FinanceTargetInterpretation = Readonly<{
  targetType: FinanceObjectiveTargetType;
  primary: boolean;
  assumptionId?: FinanceTargetInterpretationAssumptionId;
}>;

/**
 * Every interpretation the policy admits for a CITED unit, primary first.
 *
 * This is a unit table, not a wording table: the input is the governed unit the
 * citation scan already authenticated, so no phrasing reaches it. A percent
 * citation is genuinely ambiguous between three meanings and the default policy
 * names which one leads — with a typed assumption id, because it chose.
 */
const FINANCE_TARGET_INTERPRETATIONS_BY_UNIT: Readonly<
  Record<CapabilityRequirementUnit, readonly FinanceTargetInterpretation[]>
> = Object.freeze({
  months: Object.freeze([]),
  percent: Object.freeze([
    Object.freeze({
      targetType: 'margin_relative_increase_pct' as const,
      primary: true,
      assumptionId: 'percent_interpreted_as_relative_margin_change' as const,
    }),
    Object.freeze({ targetType: 'margin_level_pct' as const, primary: false }),
    Object.freeze({ targetType: 'dollar_increase_pct' as const, primary: false }),
    // The asker re-parameterizing the approved OBJECTIVE by the same percent is a
    // fourth lawful reading of one sentence, offered as its own reference so the
    // manager selects it rather than a parser guessing at it.
    Object.freeze({ targetType: 'objective_growth_pct' as const, primary: false }),
  ]),
  percentage_points: Object.freeze([
    Object.freeze({ targetType: 'margin_point_increase' as const, primary: true }),
  ]),
  bps: Object.freeze([
    Object.freeze({ targetType: 'margin_point_increase' as const, primary: true }),
  ]),
  // #5683 HOLD-3 — a USD citation offers BOTH the level and the delta reading
  // as their own typed ids; the model selects, it is never inferred from
  // wording. `objective_absolute_usd` stays primary (unchanged default), so
  // no existing caller that reads only the primary reading changes behavior.
  usd: Object.freeze([
    Object.freeze({ targetType: 'objective_absolute_usd' as const, primary: true }),
    Object.freeze({ targetType: 'objective_improvement_usd' as const, primary: false }),
  ]),
});

export function financeTargetInterpretationsForUnit(
  unit: CapabilityRequirementUnit | null | undefined
): readonly FinanceTargetInterpretation[] {
  if (unit === null || unit === undefined) return [];
  return FINANCE_TARGET_INTERPRETATIONS_BY_UNIT[unit] ?? [];
}

/**
 * A bps citation states hundredths of a point, so the governed parameter the
 * point-increase formula takes is the cited value divided by 100. This is the
 * only scale the policy applies, and it is declared here rather than inferred.
 */
export function financeTargetParameterForUnit(
  unit: CapabilityRequirementUnit,
  citedValue: number
): number {
  return unit === 'bps' ? citedValue / 100 : citedValue;
}

/**
 * MATERIALITY POLICY (#5345 clarification amendment).
 *
 * Two readings of the same stated target change the management decision when
 * they disagree about whether the target is already met, or when the required
 * economic gap moves by more than this fraction of the primary gap. Declared as
 * policy, receipted with the answer, and never a hidden threshold inside a
 * renderer.
 */
export const FINANCE_TARGET_INTERPRETATION_MATERIALITY_FRACTION = 0.1;

/**
 * True when a sensitivity reading would change the management decision, so the
 * dependent verdict pauses for one bounded clarification while every
 * independent analysis continues.
 */
export function financeTargetInterpretationIsDecisionChanging(
  primaryGapUsd: number,
  alternativeGapUsd: number
): boolean {
  const primaryClosed = primaryGapUsd <= 0;
  const alternativeClosed = alternativeGapUsd <= 0;
  if (primaryClosed !== alternativeClosed) return true;
  const denominator = Math.max(Math.abs(primaryGapUsd), 1);
  return (
    Math.abs(alternativeGapUsd - primaryGapUsd) / denominator >
    FINANCE_TARGET_INTERPRETATION_MATERIALITY_FRACTION
  );
}

export function isFinanceObjectiveTargetExecutionInput(
  value: unknown
): value is FinanceObjectiveTargetExecutionInput {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<FinanceObjectiveTargetExecutionInput>;
  return (
    typeof candidate.targetRef === 'string' &&
    candidate.targetRef.length > 0 &&
    typeof candidate.metricRef === 'string' &&
    candidate.metricRef.length > 0 &&
    isFinanceObjectiveTargetType(candidate.targetType) &&
    typeof candidate.targetValue === 'number' &&
    Number.isFinite(candidate.targetValue) &&
    (FINANCE_TARGET_PROVENANCES as readonly unknown[]).includes(
      candidate.provenance
    ) &&
    typeof candidate.interpretationPolicyVersion === 'string' &&
    candidate.interpretationPolicyVersion.length > 0 &&
    typeof candidate.horizonId === 'string' &&
    (FINANCE_TARGET_HORIZON_BASES as readonly unknown[]).includes(
      candidate.horizonBasis
    ) &&
    (candidate.reportingIdentity === undefined ||
      isApprovedTargetReportingIdentity(candidate.reportingIdentity))
  );
}

function isApprovedTargetReportingIdentity(
  value: unknown,
): value is FinanceApprovedTargetReportingIdentity {
  if (!value || typeof value !== 'object') return false;
  const identity = value as Partial<FinanceApprovedTargetReportingIdentity>;
  return [
    identity.objectiveBindingId,
    identity.sourceArtifactId,
    identity.scopeKind,
    identity.scopeId,
    identity.accountingBasis,
  ].every((field) => typeof field === 'string' && field.trim().length > 0) &&
    identity.periodType === 'annual' &&
    typeof identity.fiscalYear === 'number' &&
    Number.isInteger(identity.fiscalYear) &&
    (identity.approvedAt === undefined ||
      (typeof identity.approvedAt === 'string' &&
        Number.isFinite(Date.parse(identity.approvedAt))));
}
